import React from 'react';
import { Box, Typography, Paper, List, ListItem, ListItemText, Divider } from '@mui/material';
import { format } from 'date-fns';

interface ActivityLog {
  id: string;
  action: string;
  actor: { name: string | null; email: string };
  details: any; // JSON field, can be anything
  createdAt: string; // ISO string
}

interface TeamActivityLogProps {
  activityLogs: ActivityLog[];
}

const TeamActivityLog: React.FC<TeamActivityLogProps> = ({ activityLogs }) => {
  const renderActivityDetails = (log: ActivityLog) => {
    const actorName = log.actor.name || log.actor.email;
    switch (log.action) {
      case 'MEMBER_INVITED':
        return <Typography variant="body2"><strong>{actorName}</strong> invited <strong>{log.details.invitedEmail}</strong> as a <strong>{log.details.invitedRole}</strong>.</Typography>;
      case 'MEMBER_ROLE_UPDATED':
        return <Typography variant="body2"><strong>{actorName}</strong> changed <strong>{log.details.memberName}</strong>'s role from <strong>{log.details.oldRole}</strong> to <strong>{log.details.newRole}</strong>.</Typography>;
      case 'MEMBER_REMOVED':
        return <Typography variant="body2"><strong>{actorName}</strong> removed <strong>{log.details.memberName}</strong> from the team.</Typography>;
      case 'TEAM_PROFILE_UPDATED':
        return <Typography variant="body2"><strong>{actorName}</strong> updated the team profile.</Typography>;
      default:
        return <Typography variant="body2"><strong>{actorName}</strong> performed an action: {log.action}.</Typography>;
    }
  };

  return (
    <Box sx={{ mt: { xs: 4, lg: 0 } }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Team Activity Log
      </Typography>
      <Paper elevation={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
        {activityLogs.length === 0 ? (
          <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
            No team activities yet.
          </Typography>
        ) : (
          <List dense>
            {activityLogs.map((log, index) => (
              <React.Fragment key={log.id}>
                <ListItem alignItems="flex-start">
                  <ListItemText
                    primary={renderActivityDetails(log)}
                    secondary={format(new Date(log.createdAt), 'MMM dd, yyyy HH:mm')}
                  />
                </ListItem>
                {index < activityLogs.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default TeamActivityLog;
